import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, FolderPlus, Pencil, Trash2, FolderInput, Film, Tv, RefreshCw, Users, Copy, Download, ChevronLeft, ChevronRight, ChevronDown, Search, ArrowUp, ArrowDown, TriangleAlert, Sparkles, GripVertical, Plus, CloudUpload, CheckCircle2, AlertTriangle as AlertIcon, FileVideo } from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { fileFingerprint } from "../lib/fileHash";
import { GlassCard, PageHeader, Spinner, EmptyState, Modal } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { DomainGate } from "../components/DomainGate";
import { useLang } from "../context/LanguageContext";

const SEM_PASTA = "(Sem pasta)";

function VersionRow({ v, kind, creds, onDelete, onMove, onEditEpisode, onReupload, posterUrl, testid }) {
  const [imgErr, setImgErr] = useState(false);
  const origin = window.location.origin;
  const link = creds ? `${origin}/api/xc/${kind}/${creds.username}/${creds.password}/${v.id}.mp4` : null;
  const copy = (t, m) => { navigator.clipboard.writeText(t); toast.success(m); };
  const label = kind === "series" && v.season != null
    ? `S${String(v.season).padStart(2, "0")}E${String(v.episode || 0).padStart(2, "0")}`
    : (v.version || "filme").toUpperCase();
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-800/60 hover:border-slate-700 transition-colors" data-testid={testid}>
      {posterUrl && !imgErr ? (
        <img src={posterUrl} alt="" data-testid={`${testid}-poster`} className="w-7 h-10 rounded object-cover shrink-0 bg-slate-800" loading="lazy"
          onError={() => setImgErr(true)} />
      ) : (
        <span className="w-7 h-10 rounded bg-slate-800/60 flex items-center justify-center shrink-0 text-slate-700" data-testid={`${testid}-poster-ph`}>
          {kind === "series" ? <Tv size={10} /> : <Film size={10} />}
        </span>
      )}
      <span className="text-[11px] font-mono2 text-cyan-300 w-16 shrink-0 truncate" title={v.filename || ""}>{label}</span>
      {kind === "series" && onMove && (
        <span className="flex flex-col shrink-0">
          <button type="button" data-testid={`${testid}-up`} title="Mover episódio para cima" onClick={() => onMove(v, -1)} className="p-0.5 text-slate-600 hover:text-cyan-300 transition-colors"><ArrowUp size={11} /></button>
          <button type="button" data-testid={`${testid}-down`} title="Mover episódio para baixo" onClick={() => onMove(v, 1)} className="p-0.5 text-slate-600 hover:text-cyan-300 transition-colors"><ArrowDown size={11} /></button>
        </span>
      )}
      <span className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden">
        {kind === "series" && v.version && <span className="text-[9px] font-mono2 uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{v.version}</span>}
        {v.quality && <span className="text-[9px] font-mono2 uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{v.quality}</span>}
        {v.res_label && <span data-testid={`${testid}-res`} title={v.width && v.height ? `Resolução real: ${v.width}×${v.height}` : "Resolução real medida"} className="text-[9px] font-mono2 uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/25">{v.res_label}{v.height ? ` ${v.height}p` : ""}</span>}
        <span className="text-[10px] font-mono2 text-slate-500 shrink-0">{fmtBytes(v.size_bytes || 0)}</span>
      </span>
      {link ? (
        <>
          <button type="button" data-testid={`${testid}-copy`} title="Copiar link de reprodução" onClick={() => copy(link, "Link copiado — cole no VLC/player")} className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors shrink-0"><Copy size={13} /></button>
          <a data-testid={`${testid}-download`} title="Baixar arquivo" href={`${link}?download=1`} className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors shrink-0"><Download size={13} /></a>
        </>
      ) : (
        <span className="text-[9px] font-mono2 text-slate-600 shrink-0">sem acesso p/ link</span>
      )}
      {kind === "series" && onEditEpisode && (
        <button type="button" data-testid={`${testid}-edit`} title="Editar temporada/episódio" onClick={() => onEditEpisode(v)} className="p-1.5 rounded-lg text-slate-500 hover:text-amber-300 hover:bg-amber-400/10 transition-colors shrink-0"><Pencil size={13} /></button>
      )}
      {onReupload && (
        <button type="button" data-testid={`${testid}-reupload`} title="Re-upload / corrigir este ficheiro (substitui o conteúdo na nuvem — o link NÃO muda)" onClick={() => onReupload(v)} className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors shrink-0"><RefreshCw size={13} /></button>
      )}
      <button type="button" data-testid={`${testid}-delete`} title="Excluir (apaga o arquivo da nuvem)" onClick={() => onDelete(v)} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-300 hover:bg-rose-400/10 transition-colors shrink-0"><Trash2 size={13} /></button>
    </div>
  );
}

function TitleDetail({ detail, onBack, onRename, onDeleteTitle, onDeleteVersion, onMoveVersion, onEditEpisode, onNewSeason, onImportSeason, onPcUpload, onReupload }) {
  const nextSeason = detail.kind === "series"
    ? Math.max(0, ...(detail.versions || []).map((v) => v.season || 0)) + 1
    : 0;
  const seasons = {};
  (detail.versions || []).forEach((v) => {
    const s = detail.kind === "series" ? (v.season || 0) : 0;
    (seasons[s] = seasons[s] || []).push(v);
  });
  const groups = Object.keys(seasons).map(Number).sort((a, b) => a - b);
  const totalBytes = (detail.versions || []).reduce((acc, v) => acc + (v.size_bytes || 0), 0);
  return (
    <div className="space-y-4" data-testid="pastas-detail">
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="pastas-detail-back" onClick={onBack} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"><ChevronLeft size={15} /></button>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${detail.kind === "series" ? "bg-amber-400/10 text-amber-300" : "bg-cyan-400/10 text-cyan-300"}`}>
          {detail.kind === "series" ? <Tv size={14} /> : <Film size={14} />}
        </span>
        <div className="flex-1 min-w-40">
          <p className="text-sm font-semibold text-white truncate" data-testid="pastas-detail-title">{detail.title} {detail.year ? <span className="text-slate-500 font-normal">({detail.year})</span> : null}</p>
          <p className="text-[10px] font-mono2 text-slate-500">{detail.kind === "series" ? "Série" : "Filme"} · {(detail.versions || []).length} {detail.kind === "series" ? "episódio(s)" : "versão(ões)"} · {fmtBytes(totalBytes)}</p>
        </div>
        {detail.kind === "series" && (
          <>
            <button data-testid="pastas-new-season" onClick={() => onNewSeason(nextSeason)} title={`Criar Temporada ${nextSeason} vazia nesta série`}
              className="px-3 py-2 rounded-lg text-[11px] border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 flex items-center gap-1.5 transition-colors">
              <Plus size={11} /> Temporada {nextSeason}
            </button>
            <button data-testid="pastas-import-season" onClick={() => onImportSeason(nextSeason)} title="Baixar episódios diretamente para a temporada escolhida (URLs ou lista M3U)"
              className="btn-neon px-3 py-2 rounded-lg text-[11px] flex items-center gap-1.5">
              <Download size={11} /> Importar p/ temporada
            </button>
            <button data-testid="pastas-upload-pc" onClick={() => onPcUpload(nextSeason)} title="Enviar ficheiros de vídeo do computador diretamente para a temporada escolhida"
              className="px-3 py-2 rounded-lg text-[11px] border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 flex items-center gap-1.5 transition-colors">
              <CloudUpload size={11} /> Upload do PC
            </button>
          </>
        )}
        <button data-testid="pastas-detail-rename" onClick={onRename} className="px-3 py-2 rounded-lg text-[11px] border border-slate-700 text-slate-300 hover:border-cyan-400/40 flex items-center gap-1.5 transition-colors"><Pencil size={11} /> Renomear</button>
        <button data-testid="pastas-detail-delete-all" onClick={onDeleteTitle} className="px-3 py-2 rounded-lg text-[11px] border border-slate-700 text-slate-300 hover:border-rose-400/40 hover:text-rose-300 flex items-center gap-1.5 transition-colors"><Trash2 size={11} /> Excluir tudo</button>
      </div>
      {!detail.creds && <p className="text-[11px] text-amber-300/80" data-testid="pastas-detail-nocreds">Para copiar link/baixar, crie antes um acesso na aba Acessos.</p>}
      {!groups.length ? (
        <EmptyState icon={FolderOpen} title="Sem arquivos" subtitle="Este título não tem episódios/versões ativos." />
      ) : (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {groups.map((s) => (
            <div key={s}>
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-2" data-testid={`pastas-season-${s}`}>
                {detail.kind === "series" ? `Temporada ${s === 0 ? "única" : s}` : "Versões"} · {seasons[s].length} item(ns) · {fmtBytes(seasons[s].reduce((a, v) => a + (v.size_bytes || 0), 0))}
              </p>
              <div className="space-y-1.5">
                {seasons[s].map((v) => (
                  <VersionRow key={v.id} v={v} kind={detail.kind} creds={detail.creds} onDelete={onDeleteVersion} onMove={onMoveVersion} onEditEpisode={onEditEpisode} onReupload={onReupload} posterUrl={detail.poster_url} testid={`pastas-ver-${v.id}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Pastas() {
  const [tree, setTree] = useState(null);
  const [items, setItems] = useState(null);
  const [folder, setFolder] = useState("");
  const [kind, setKind] = useState("movie"); // card ativo: Filmes (movie) ou Séries (tv)
  const [selected, setSelected] = useState({});
  const [moveTo, setMoveTo] = useState("");
  const [moveFolderQ, setMoveFolderQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // create | rename | delete
  const [itemModal, setItemModal] = useState(null); // {type: 'rename'|'delete', item}
  const [name, setName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [seriesModal, setSeriesModal] = useState(false); // criar série vazia na pasta (fluxo robusto)
  const [seriesName, setSeriesName] = useState("");
  const [posterUrl, setPosterUrl] = useState(""); // edição do link da capa no modal renomear
  const [posterBusy, setPosterBusy] = useState(""); // retry de capa por título (clique no placeholder)
  const [posterErr, setPosterErr] = useState({}); // capas cuja imagem falhou a carregar → viram placeholder clicável
  const [dupsOpen, setDupsOpen] = useState(false); // modal de duplicados
  const [dups, setDups] = useState(null); // null = a carregar
  const [dupKeep, setDupKeep] = useState({}); // índice do grupo -> title_id a manter
  const { t } = useLang();
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [delVer, setDelVer] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [sortBy, setSortBy] = useState("name");
  const [bulkDel, setBulkDel] = useState(false);
  const [selFolders, setSelFolders] = useState({});
  const [bulkFolderDel, setBulkFolderDel] = useState(false);
  const [epEdit, setEpEdit] = useState(null); // {version, season, episode}
  const [epBusy, setEpBusy] = useState(false);
  const [seasonImp, setSeasonImp] = useState(null); // {mode: "create"|"import", season, source, urls, m3u, version, quality, startEp}
  const [seasonBusy, setSeasonBusy] = useState(false);
  const [pcUpload, setPcUpload] = useState(null); // {season, files:[{name,size,episode,pct,status,err}]}
  const [reup, setReup] = useState(null); // {version, season, episode, file, pct} — re-upload/correção de UM ficheiro (link não muda)
  const pcFileRef = useRef(null);
  const pcAbortRef = useRef(false);
  const [itemPage, setItemPage] = useState(1);
  const [folderSearch, setFolderSearch] = useState("");
  const [searchScope, setSearchScope] = useState("folder");
  const [globalResults, setGlobalResults] = useState(null);
  const ITEM_PAGE_SIZE = 200;

  const qs = useCallback((extra = "") => `${extra ? extra + "&" : "?"}${accountId ? `user_id=${accountId}` : ""}`.replace(/[?&]$/, ""), [accountId]);

  const loadTree = useCallback(() => {
    api.get(`/folders/tree${qs()}`).then((r) => setTree(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [qs]);

  const loadItems = useCallback((f) => {
    setItems(null);
    setSelected({});
    api.get(`/folders/items${qs(`?folder=${encodeURIComponent(f)}&kind=${kind}`)}`)
      .then((r) => setItems(r.data))
      .catch((e) => { setItems([]); toast.error(formatApiError(e)); });
  }, [qs, kind]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => { loadItems(folder); }, [folder, loadItems]);

  useEffect(() => {
    if (searchScope !== "global" || search.trim().length < 2) { setGlobalResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/folders/search${qs(`?q=${encodeURIComponent(search.trim())}`)}`)
        .then((r) => setGlobalResults(r.data))
        .catch(() => setGlobalResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, searchScope, qs]);

  const pick = (f) => {
    setFolder(f);
    setSelected({});
    setSearch("");
    setItemPage(1);
    loadItems(f);
  };

  const switchKind = (k) => {
    if (k === kind) return;
    setKind(k);
    setFolder("");
    setItems(null);
    setSelected({});
    setDetail(null);
    setSearch("");
    setFolderSearch("");
    setSelFolders({});
  };

  const openDups = async () => {
    setDupsOpen(true); setDups(null); setDupKeep({});
    try { const { data } = await api.get(`/folders/duplicates${qs()}`); setDups(data || []); }
    catch (e) { toast.error(formatApiError(e)); setDupsOpen(false); }
  };

  const repairSplit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/folders/repair-split-titles${qs()}`);
      toast.success((data.merged_groups || data.reattached)
        ? `Reparação concluída: ${data.reattached} ficha(s) repostas · ${data.merged_groups} grupo(s) fundidos · ${data.versions_moved} ficheiro(s) movidos`
        : "Nada a reparar — não há fichas divididas com nome exato igual");
      if (data.merged_groups || data.reattached) { loadTree(); if (folder) loadItems(folder); }
      const { data: d } = await api.get(`/folders/duplicates${qs()}`); setDups(d || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const mergeSplit = async () => {
    if (!detail?.split_siblings?.length) return;
    setBusy(true);
    try {
      const remove_ids = detail.split_siblings.map((s) => s.id);
      const { data } = await api.post(`/folders/merge${qs()}`, { keep_id: detail.id, remove_ids });
      toast.success(`Título unificado: ${data.moved} ficheiro(s) movidos para "${detail.title}"`);
      const { data: fresh } = await api.get(`/folders/title/${detail.id}/detail${qs()}`);
      setDetail(fresh);
      loadTree();
      if (folder) loadItems(folder);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const mergeGroup = async (g, gi) => {
    const keepId = dupKeep[gi] || g.items[0].id;
    setBusy(true);
    try {
      const remove_ids = g.items.filter((i) => i.id !== keepId).map((i) => i.id);
      const { data } = await api.post(`/folders/merge${qs()}`, { keep_id: keepId, remove_ids });
      toast.success(`Fusão concluída: ${data.moved} ficheiro(s) movidos, ${data.conflicts} conflito(s) resolvidos, ${fmtBytes(data.freed_bytes)} libertados`);      setDups((prev) => (prev || []).filter((_, i) => i !== gi));
      loadItems(folder); loadTree();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const mergeAll = async () => {
    setBusy(true);
    let ok = 0, freed = 0, fails = 0;
    try {
      const groups = [...(dups || [])];
      for (let gi = 0; gi < groups.length; gi++) {
        const g = groups[gi];
        const keepId = dupKeep[gi] || g.items[0].id; // por defeito mantém o que tem mais ficheiros
        try {
          const { data } = await api.post(`/folders/merge${qs()}`, { keep_id: keepId, remove_ids: g.items.filter((i) => i.id !== keepId).map((i) => i.id) });
          ok += 1; freed += data.freed_bytes || 0;
        } catch { fails += 1; }
      }
      toast.success(`Fusão em massa: ${ok} grupo(s) fundidos, ${fmtBytes(freed)} libertados${fails ? ` · ${fails} falharam` : ""}`);
      setDups([]); loadItems(folder); loadTree();
    } finally { setBusy(false); }
  };

  const retryPoster = async (it) => {
    setPosterBusy(it.title_id);
    try {
      const { data } = await api.post(`/posters/generate/${it.title_id}${qs()}`);
      if (data.poster_url) {
        toast.success(data.source === "vod" ? `TMDB sem capa — gerada a capa padrão VOD para "${it.title}"` : `Capa TMDB aplicada em "${it.title}"`);
        loadItems(folder);
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setPosterBusy(""); }
  };

  const createSeries = async () => {
    if (!seriesName.trim()) return;
    setBusy(true);
    try {
      await api.post(`/folders/series${qs()}`, { folder, name: seriesName.trim() });
      toast.success(`Série “${seriesName.trim()}” criada — abra-a para criar temporadas e importar episódios`);
      setSeriesModal(false);
      setSeriesName("");
      loadItems(folder);
      loadTree();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const submitModal = async () => {
    setBusy(true);
    try {
      if (modal === "create") {
        await api.post(`/folders${qs()}`, { name });
        toast.success(`Pasta "${name}" criada`);
      } else if (modal === "rename") {
        await api.post(`/folders/rename${qs()}`, { old: folder, new: name });
        toast.success(`Pasta renomeada para "${name}" — arquivos sendo reorganizados na nuvem`);
        setFolder(name);
      } else if (modal === "delete") {
        await api.delete(`/folders${qs(`?name=${encodeURIComponent(folder)}`)}`);
        toast.success("Pasta excluída — os itens foram para 'Sem pasta' (nada foi apagado)");
        setFolder("");
      }
      setModal(null);
      setName("");
      loadTree();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitItemModal = async () => {
    if (!itemModal) return;
    setBusy(true);
    try {
      if (itemModal.type === "rename") {
        await api.patch(`/folders/items/${itemModal.item.title_id}${qs()}`, { title: name });
        const newPoster = posterUrl.trim();
        if (newPoster && newPoster !== (itemModal.item.poster_url || "")) {
          await api.post(`/posters/manual/${itemModal.item.title_id}`, { url: newPoster });
        }
        toast.success("Título atualizado");
      } else {
        await api.delete(`/folders/items/${itemModal.item.title_id}${qs()}`);
        toast.success("Título excluído — arquivos apagados da nuvem e cota liberada");
        if (detail) setDetail(null);
      }
      setItemModal(null);
      setName("");
      loadItems(folder);
      loadTree();
      if (detail && itemModal.type === "rename") {
        try {
          const { data: fresh } = await api.get(`/folders/title/${itemModal.item.title_id}/detail${qs()}`);
          setDetail(fresh);
        } catch { /* título pode ter sumido */ }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openPcUpload = (s) => { pcAbortRef.current = false; setPcUpload({ season: s, files: [] }); };
  const openReupload = (v) => { pcAbortRef.current = false; setReup({ version: v, season: detail.kind === "series" ? (v.season || 1) : null, episode: detail.kind === "series" ? (v.episode || 1) : null, file: null, pct: 0 }); };
  const addPcFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.size > 0);
    if (!files.length) return;
    const next = [...(pcUpload?.files || [])];
    files.forEach((f) => {
      const m = f.name.match(/[sS](\d{1,2})\s*[eE](\d{1,3})/);
      next.push({ file: f, name: f.name, size: f.size, episode: m ? Number(m[2]) : null, pct: 0, status: "pending", err: null });
    });
    next.forEach((f, i) => { if (!f.episode) f.episode = i + 1; });
    setPcUpload({ ...pcUpload, files: next });
  };
  const startPcUpload = async () => {
    if (!pcUpload?.files?.length || !detail) return;
    const botId = detail.bot_id || detail.versions?.[0]?.bot_id;
    if (!botId) { toast.error("Bot do título não identificado"); return; }
    setSeasonBusy(true);
    pcAbortRef.current = false;
    for (let i = 0; i < pcUpload.files.length; i++) {
      if (pcAbortRef.current) break;
      const f = pcUpload.files[i];
      if (f.status === "done") continue;
      const upd = (patch) => setPcUpload((p) => p ? { ...p, files: p.files.map((x, xi) => xi === i ? { ...x, ...patch } : x) } : p);
      upd({ status: "uploading", pct: 0, err: null });
      try {
        const fp = await fileFingerprint(f.file);
        const { data: sess } = await api.post("/uploads/init", {
          bot_id: botId, filename: f.name, size_bytes: f.size,
          title_id: detail.id, season: pcUpload.season, episode: f.episode, file_hash: fp || undefined,
        });
        if (sess.duplicate) { upd({ status: "done", pct: 100 }); continue; }
        const totalParts = Math.ceil(f.size / sess.chunk_size);
        const partsDone = new Set(sess.parts_done || []);
        for (let n = 1; n <= totalParts; n++) {
          if (pcAbortRef.current) throw new Error("cancelado");
          if (partsDone.has(n)) continue;
          const blob = f.file.slice((n - 1) * sess.chunk_size, n * sess.chunk_size);
          await api.put(`/uploads/${sess.session_id}/part/${n}`, blob, {
            headers: { "Content-Type": "application/octet-stream" },
            onUploadProgress: (e) => {
              const total = (n - 1) * sess.chunk_size + e.loaded;
              upd({ pct: Math.min(99, Math.round((total / f.size) * 100)) });
            },
          });
        }
        await api.post(`/uploads/${sess.session_id}/finish`);
        upd({ status: "done", pct: 100 });
      } catch (e) {
        if (pcAbortRef.current) { upd({ status: "pending", err: "cancelado" }); break; }
        upd({ status: "error", err: formatApiError(e) });
      }
    }
    setSeasonBusy(false);
    if (!pcAbortRef.current) {
      toast.success("Upload do PC concluído — os episódios já estão na temporada");
      const { data: fresh } = await api.get(`/folders/title/${detail.id}/detail${qs()}`);
      setDetail(fresh);
    }
  };

  const startReupload = async () => {
    if (!reup?.file || !detail) return;
    const botId = detail.bot_id || detail.versions?.[0]?.bot_id;
    if (!botId) { toast.error("Bot do título não identificado"); return; }
    setSeasonBusy(true);
    try {
      const payload = { bot_id: botId, filename: reup.file.name, size_bytes: reup.file.size, title_id: detail.id };
      if (detail.kind === "series") { payload.season = reup.season; payload.episode = reup.episode; }
      const { data: sess } = await api.post("/uploads/init", payload);
      const totalParts = Math.ceil(reup.file.size / sess.chunk_size);
      for (let n = 1; n <= totalParts; n++) {
        const blob = reup.file.slice((n - 1) * sess.chunk_size, n * sess.chunk_size);
        await api.put(`/uploads/${sess.session_id}/part/${n}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (e) => setReup((r) => r ? { ...r, pct: Math.min(99, Math.round((((n - 1) * sess.chunk_size + e.loaded) / reup.file.size) * 100)) } : r),
        });
      }
      await api.post(`/uploads/${sess.session_id}/finish`);
      toast.success("Ficheiro substituído — o link do cliente mantém-se exatamente o mesmo");
      setReup(null);
      const { data: fresh } = await api.get(`/folders/title/${detail.id}/detail${qs()}`);
      setDetail(fresh);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeasonBusy(false);
    }
  };

  const submitSeason = async () => {
    if (!seasonImp || !detail) return;
    setSeasonBusy(true);
    try {
      if (seasonImp.mode === "create") {
        // temporada "criada" = placeholder visual: basta existir quando o 1º episódio chegar — aqui só confirmamos a intenção e abrimos logo a importação
        toast.success(`Temporada ${seasonImp.season} pronta — agora importa os episódios`);
        setSeasonImp({ mode: "import", season: seasonImp.season, source: "urls", urls: "", m3u: "", version: "dual", quality: "FHD", startEp: "" });
        setSeasonBusy(false);
        return;
      }
      const body = { season: seasonImp.season, version: seasonImp.version, quality: seasonImp.quality };
      let res;
      if (seasonImp.source === "m3u") {
        if (!seasonImp.m3u.trim()) { toast.error("Cola o link da lista M3U do fornecedor"); setSeasonBusy(false); return; }
        res = await api.post(`/folders/title/${detail.id}/seasons/import-m3u${qs()}`, { ...body, urls: [seasonImp.m3u.trim()] });
      } else {
        const urls = seasonImp.urls.split("\n").map((u) => u.trim()).filter(Boolean);
        if (!urls.length) { toast.error("Cola pelo menos um link (um por linha)"); setSeasonBusy(false); return; }
        res = await api.post(`/folders/title/${detail.id}/seasons/import-urls${qs()}`,
          { ...body, urls, start_episode: seasonImp.startEp ? Number(seasonImp.startEp) : null });
      }
      toast.success(`Importação da Temporada ${seasonImp.season} na fila — acompanha na página Fila`);
      setSeasonImp(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeasonBusy(false);
    }
  };

  const saveEpisode = async () => {
    if (!epEdit || !detail) return;
    const botId = detail.bot_id || detail.versions?.[0]?.bot_id;
    if (!botId) { toast.error("Bot do título não identificado"); return; }
    setEpBusy(true);
    try {
      await api.put(`/instances/${botId}/versions/${epEdit.version.id}`, { season: epEdit.season, episode: epEdit.episode });
      toast.success("Episódio atualizado");
      setEpEdit(null);
      const { data: fresh } = await api.get(`/folders/title/${detail.id}/detail${qs()}`);
      setDetail(fresh);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEpBusy(false);
    }
  };

  const doMove = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/folders/move${qs()}`, { title_ids: ids, to: moveTo });
      toast.success(`${data.moved} item(ns) movidos para "${data.to}" — reorganizando na nuvem`);
      setSelected({});
      loadItems(folder);
      loadTree();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const moveMixed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/folders/move-mixed${qs()}`, { folder, kind });
      toast.success(data.moved ? `${data.moved} item(ns) movidos para "${data.to}" — a pasta ficou limpa` : "Nada a mover");
      loadItems(folder);
      loadTree();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const separateAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/folders/separate-all${qs()}`);
      toast.success(data.moved ? `${data.moved} título(s) separados para as pastas certas — a nuvem reorganiza em segundo plano` : "Tudo já está separado");
      loadTree();
      if (folder) loadItems(folder);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selFolderCount = Object.values(selFolders).filter(Boolean).length;
  const toggleFolder = (name) => setSelFolders((p) => { const n = { ...p }; if (n[name]) delete n[name]; else n[name] = true; return n; });
  const doBulkFolderDelete = async () => {
    const names = Object.keys(selFolders).filter((k) => selFolders[k]);
    if (!names.length) return;
    setBusy(true);
    let done = 0;
    const errors = [];
    for (const name of names) {
      try {
        await api.delete(`/folders${qs(`?name=${encodeURIComponent(name)}`)}`);
        done += 1;
      } catch (e) {
        errors.push(name);
      }
    }
    toast.success(done ? `${done} pasta(s) excluída(s) — conteúdo voltou para Sem pasta` : "Nenhuma pasta excluída");
    if (errors.length) toast.error(`Falha em ${errors.length}: ${errors.slice(0, 2).join(", ")}`);
    setBulkFolderDel(false);
    setSelFolders({});
    setBusy(false);
    if (folder && names.includes(folder)) setFolder(null);
    loadTree();
    if (folder) loadItems(folder);
  };

  const doBulkDelete = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/folders/items/bulk-delete${qs()}`, { title_ids: ids });
      toast.success(`${data.deleted} título(s) excluídos — ${fmtBytes(data.freed_bytes)} liberados da cota`);
      setBulkDel(false);
      setSelected({});
      loadItems(folder);
      loadTree();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (it) => {
    try {
      const { data } = await api.get(`/folders/title/${it.title_id}/detail${qs()}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveVersion = async (v, direction) => {
    try {
      await api.post(`/folders/version-reorder${qs()}`, { vid: v.id, direction });
      if (detail) await openDetail({ title_id: detail.id });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [dragFolder, setDragFolder] = useState(null);

  const onFolderDrop = async (targetName) => {
    const drag = dragFolder;
    setDragFolder(null);
    if (!drag || drag === targetName || folderQ) return;
    const names = kindFolders.map((f) => f.name).filter((n) => n !== SEM_PASTA);
    const from = names.indexOf(drag);
    const to = names.indexOf(targetName);
    if (from < 0 || to < 0) return;
    names.splice(to, 0, names.splice(from, 1)[0]);
    try {
      await api.post(`/folders/reorder-bulk${qs()}`, { names });
      toast.success("Ordem das pastas atualizada");
    } catch (e) { toast.error(formatApiError(e)); }
    loadTree();
  };

  const reorderFolder = async (name, direction) => {
    try {
      await api.post(`/folders/reorder${qs()}`, { name, direction });
      loadTree();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const closeDetail = () => {
    setDetail(null);
    loadItems(folder);
    loadTree();
  };

  const deleteVersion = async () => {
    if (!delVer) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/folders/title/${delVer.title_id}/versions/${delVer.id}${qs()}`);
      toast.success("Arquivo excluído da nuvem e cota liberada");
      setDelVer(null);
      if (data.title_deleted) closeDetail();
      else {
        const { data: fresh } = await api.get(`/folders/title/${detail.id}/detail${qs()}`);
        setDetail(fresh);
        loadTree();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cleanOrphans = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/folders/cleanup-orphans${qs()}`);
      toast.success(data.removed ? `${data.removed} título(s) vazio(s) removidos — contagem corrigida` : "Nenhum título vazio encontrado");
      loadTree();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selCount = Object.values(selected).filter(Boolean).length;
  const folders = tree?.folders || [];
  // cada pasta pertence a UM card: o do tipo dominante do conteúdo dela (séries>filmes → Séries).
  // pastas vazias e "(Sem pasta)" aparecem nos dois cards para dar para criar estrutura nova
  const kindFolders = folders.filter((f) => f.name === SEM_PASTA || !f.kind || f.kind === kind);
  const folderQ = folderSearch.trim().toLowerCase();
  const visibleFolders = folderQ ? kindFolders.filter((f) => f.name.toLowerCase().includes(folderQ)) : kindFolders;
  const kindCount = (f) => (kind === "tv" ? f.series : f.movies) ?? f.count;
  const kindBytes = (f) => (kind === "tv" ? f.series_bytes : f.movies_bytes) ?? f.bytes;
  const kindFoldersTotal = (k) => folders.filter((f) => f.name !== SEM_PASTA && f.kind === k).length;
  // itens do outro tipo misturados na pasta aberta (ficam fora da vista do card ativo)
  const currentFolder = folders.find((f) => f.name === (folder || SEM_PASTA)) || null;
  const mixedOther = folder && currentFolder ? ((kind === "tv" ? currentFolder.movies : currentFolder.series) || 0) : 0;
  // total de títulos fora do card certo no catálogo inteiro (para o botão "Separar tudo")
  const mixedTotal = folders.reduce((acc, f) => acc + (f.kind === "movie" ? (f.series || 0) : f.kind === "tv" ? (f.movies || 0) : 0), 0);
  const depth = (n) => (n.match(/\//g) || []).length;
  const leaf = (n) => n.split("/").pop();
  const hasKids = (n) => kindFolders.some((f) => f.name.startsWith(`${n}/`));
  const isHidden = (n) => {
    const parts = n.split("/");
    for (let i = 1; i < parts.length; i += 1) if (collapsed[parts.slice(0, i).join("/")]) return true;
    return false;
  };
  const toggleCollapse = (n) => setCollapsed((p) => ({ ...p, [n]: !p[n] }));
  const sortedItems = [...(items || []).filter((it) => !search.trim() || it.title.toLowerCase().includes(search.trim().toLowerCase()))].sort((a, b) => {
    if (sortBy === "size") return (b.size_bytes || 0) - (a.size_bytes || 0);
    if (sortBy === "versions") return (b.versions || 0) - (a.versions || 0);
    return (a.title || "").localeCompare(b.title || "");
  });
  const totalItemPages = Math.max(1, Math.ceil(sortedItems.length / ITEM_PAGE_SIZE));
  const safeItemPage = Math.min(itemPage, totalItemPages);
  const pagedItems = sortedItems.slice((safeItemPage - 1) * ITEM_PAGE_SIZE, safeItemPage * ITEM_PAGE_SIZE);
  const allSelected = sortedItems.length > 0 && sortedItems.every((it) => selected[it.title_id]);
  const pageAllSelected = pagedItems.length > 0 && pagedItems.every((it) => selected[it.title_id]);
  const toggleAll = () => {
    const next = { ...selected };
    sortedItems.forEach((it) => { if (allSelected) delete next[it.title_id]; else next[it.title_id] = true; });
    setSelected(next);
  };
  const togglePage = () => {
    const next = { ...selected };
    pagedItems.forEach((it) => { if (pageAllSelected) delete next[it.title_id]; else next[it.title_id] = true; });
    setSelected(next);
  };

  return (
    <div className="space-y-6" data-testid="pastas-page">
      <PageHeader eyebrow={t("p_eyebrow")} title={t("pastas")} subtitle={t("p_sub")} />
      <DomainGate />
      <TutorialHint
        id="pastas"
        title={t("p_tutorial")}
        steps={[
          t("p_tut1"),
          t("p_tut2"),
          t("p_tut3"),
          t("p_tut4"),
          t("p_tut5"),
        ]}
      />

      <div className="grid grid-cols-2 gap-4" data-testid="pastas-kind-cards">
        {[
          { id: "movie", label: "Filmes", Icon: Film },
          { id: "tv", label: "Séries", Icon: Tv },
        ].map(({ id, label, Icon }) => {
          const active = kind === id;
          const data = tree?.by_kind?.[id];
          return (
            <button
              key={id}
              type="button"
              data-testid={`pastas-kind-${id}`}
              onClick={() => switchKind(id)}
              className={`glass rounded-2xl p-5 text-left transition-all ${active
                ? id === "tv" ? "border-amber-400/50 bg-amber-400/5" : "border-cyan-400/50 bg-cyan-400/5"
                : "hover:border-slate-600"}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${id === "tv" ? "bg-amber-400/10 border-amber-400/25" : "bg-cyan-400/10 border-cyan-400/25"}`}>
                  <Icon size={18} className={id === "tv" ? "text-amber-300" : "text-cyan-300"} />
                </div>
                <div className="min-w-0">
                  <p className="font-display text-white font-bold text-sm">{label}</p>
                  <p className="text-[10px] font-mono2 text-slate-500 mt-0.5 truncate" data-testid={`pastas-kind-stats-${id}`}>
                    {data ? `${kindFoldersTotal(id)} pastas · ${data.count} títulos · ${fmtBytes(data.bytes)}` : "—"}
                  </p>
                </div>
                <span className={`ml-auto w-2.5 h-2.5 rounded-full shrink-0 transition-colors ${active ? (id === "tv" ? "bg-amber-400" : "bg-cyan-400") : "bg-slate-700"}`} data-testid={`pastas-kind-dot-${id}`} />
              </div>
            </button>
          );
        })}
      </div>

      {mixedTotal > 0 && (
        <button
          type="button"
          data-testid="pastas-separate-all-btn"
          onClick={separateAll}
          disabled={busy}
          className="w-full glass rounded-2xl px-5 py-3.5 border-amber-400/30 bg-amber-400/5 hover:bg-amber-400/10 transition-colors flex items-center gap-3 text-left"
        >
          <TriangleAlert size={16} className="text-amber-300 shrink-0" />
          <span className="text-xs text-amber-200 flex-1">
            <b>{mixedTotal}</b> título(s) estão em pastas do tipo errado. Separar tudo agora — cada item vai para a pasta certa do seu card (Filmes/… ou Séries/…).
          </span>
          {busy ? <Spinner /> : <Sparkles size={15} className="text-amber-300 shrink-0" />}
        </button>
      )}

      {tree?.accounts && (
        <GlassCard className="p-4 flex flex-wrap items-center gap-3" data-testid="pastas-admin-accounts">
          <Users size={15} className="text-amber-400" />
          <span className="text-xs text-slate-400 font-mono2 uppercase tracking-widest">Admin — ver conta:</span>
          <select data-testid="pastas-account-select" className="input-dark rounded-lg px-3 py-2 text-sm" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Minha conta</option>
            {tree.accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.email} ({a.s3_prefix || "sem pasta"})</option>
            ))}
          </select>
        </GlassCard>
      )}

      <div className="grid md:grid-cols-[360px,1fr] gap-5 items-start">
        <GlassCard className="p-4" data-testid="pastas-tree-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-white font-semibold text-sm flex items-center gap-2"><FolderOpen size={15} className="text-cyan-400" /> Pastas
              <span data-testid="pastas-kind-chip" className={`text-[9px] font-mono2 uppercase tracking-widest px-2 py-0.5 rounded-full border ${kind === "tv" ? "text-amber-300 border-amber-400/30 bg-amber-400/10" : "text-cyan-300 border-cyan-400/30 bg-cyan-400/10"}`}>{kind === "tv" ? "Séries" : "Filmes"}</span>
            </h3>
            {tree?.orphans > 0 && (
              <button data-testid="cleanup-orphans-btn" onClick={cleanOrphans} disabled={busy} className="p-1.5 rounded-lg text-amber-300 hover:bg-amber-400/10 border border-transparent hover:border-amber-400/30 transition-colors" title={`Limpar ${tree.orphans} título(s) vazio(s) sem arquivo`}><Trash2 size={15} /></button>
            )}
            <button data-testid="folder-create-btn" onClick={() => { setModal("create"); setName(""); }} className="p-1.5 rounded-lg text-cyan-300 hover:bg-cyan-400/10 border border-transparent hover:border-cyan-400/30 transition-colors" title="Nova pasta"><FolderPlus size={15} /></button>
          </div>
          {tree && (
            <p className="text-[10px] font-mono2 text-slate-500 mb-3 truncate" data-testid="pastas-root">raiz: {tree.root}/ · {(tree.by_kind?.[kind]?.count ?? tree.total.count)} títulos · {fmtBytes(tree.by_kind?.[kind]?.bytes ?? tree.total.bytes)} <span className="text-slate-600">({kind === "tv" ? "séries" : "filmes"})</span></p>
          )}
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="pastas-folder-search"
              className="input-dark w-full rounded-lg pl-8 pr-3 py-2 text-xs"
              placeholder={t("p_search_folder")}
              value={folderSearch}
              onChange={(e) => setFolderSearch(e.target.value)}
            />
          </div>
          {!tree ? <Spinner /> : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
              {visibleFolders.length > 0 && (
                <div className="flex items-center justify-between mb-1 px-1" data-testid="folder-bulk-bar">
                  <button type="button" onClick={() => setSelFolders({})} className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors">limpar seleção</button>
                  {selFolderCount > 0 && (
                    <button type="button" data-testid="folder-bulk-del-btn" onClick={() => setBulkFolderDel(true)} className="text-[10px] font-semibold text-rose-300 hover:text-rose-200 inline-flex items-center gap-1 transition-colors">
                      <Trash2 size={11} /> Excluir {selFolderCount} pasta(s)
                    </button>
                  )}
                </div>
              )}
              {visibleFolders.filter((f) => folderQ || !isHidden(f.name)).map((f) => (
                <div key={f.name}
                  draggable={f.name !== SEM_PASTA && !folderQ}
                  onDragStart={() => setDragFolder(f.name)}
                  onDragOver={(e) => { if (dragFolder && dragFolder !== f.name && f.name !== SEM_PASTA && !folderQ) e.preventDefault(); }}
                  onDrop={() => onFolderDrop(f.name)}
                  onDragEnd={() => setDragFolder(null)}
                  className={`flex items-center rounded-lg transition-colors ${folder === f.name ? "bg-cyan-400/10 border border-cyan-400/25" : "border border-transparent hover:bg-slate-800/50"} ${dragFolder === f.name ? "opacity-40 border-dashed !border-cyan-400/60" : ""}`} style={{ marginLeft: `${depth(f.name) * 14}px` }}>
                  {f.name !== SEM_PASTA && !folderQ && (
                    <GripVertical size={12} className="text-slate-700 cursor-grab shrink-0 ml-1" data-testid={`folder-grip-${f.name}`} />
                  )}
                  {f.name !== SEM_PASTA && (
                    <span className="flex flex-col shrink-0 ml-0.5" data-testid={`folder-reorder-${f.name}`}>
                      <button type="button" data-testid={`folder-up-${f.name}`} title="Mover pasta para cima" onClick={() => reorderFolder(f.name, -1)} className="p-0.5 text-slate-600 hover:text-cyan-300 transition-colors"><ArrowUp size={10} /></button>
                      <button type="button" data-testid={`folder-down-${f.name}`} title="Mover pasta para baixo" onClick={() => reorderFolder(f.name, 1)} className="p-0.5 text-slate-600 hover:text-cyan-300 transition-colors"><ArrowDown size={10} /></button>
                    </span>
                  )}
                  <input type="checkbox" data-testid={`folder-check-${f.name}`} checked={!!selFolders[f.name]} onChange={() => toggleFolder(f.name)} className="accent-rose-400 shrink-0 ml-1 cursor-pointer" title="Selecionar para exclusão em massa" />
                  {hasKids(f.name) ? (
                    <button type="button" data-testid={`folder-toggle-${f.name}`} onClick={() => toggleCollapse(f.name)} className="p-1 text-slate-500 hover:text-white shrink-0 transition-colors" title={collapsed[f.name] ? "Expandir" : "Recolher"}>
                      {collapsed[f.name] ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
                    </button>
                  ) : <span className="w-[19px] shrink-0" />}
                  <button
                    data-testid={`folder-item-${f.name}`}
                    onClick={() => pick(f.name)}
                    title={f.name}
                    className={`flex-1 min-w-0 text-left px-1.5 py-2 text-xs flex items-center gap-2 transition-colors ${folder === f.name ? "text-cyan-200" : "text-slate-400"}`}
                  >
                    <FolderOpen size={12} className={`shrink-0 self-center ${folder === f.name ? "text-cyan-300" : depth(f.name) ? "text-amber-400/70" : "text-slate-600"}`} />
                    <span className="flex-1 min-w-0 truncate text-[11px]" title={f.name}>{leaf(f.name)}</span>
                    <span className="font-mono2 text-[9px] text-slate-500 shrink-0 self-center whitespace-nowrap">{kindCount(f)} · {fmtBytes(kindBytes(f))}</span>
                  </button>
                </div>
              ))}
              {!folders.length && <p className="text-xs text-slate-600 py-4 text-center">Nenhuma pasta ainda — crie a primeira.</p>}
              {folders.length > 0 && !visibleFolders.length && <p className="text-xs text-slate-600 py-4 text-center" data-testid="pastas-folder-search-empty">Nenhuma pasta encontrada para &quot;{folderSearch}&quot;.</p>}
            </div>
          )}
          {folder && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-800/60">
              <button data-testid="folder-rename-btn" onClick={() => { setModal("rename"); setName(folder); }} className="flex-1 px-2 py-2 rounded-lg text-[11px] border border-slate-700 text-slate-300 hover:border-cyan-400/40 flex items-center justify-center gap-1.5 transition-colors"><Pencil size={11} /> Renomear</button>
              <button data-testid="folder-delete-btn" onClick={() => setModal("delete")} className="flex-1 px-2 py-2 rounded-lg text-[11px] border border-slate-700 text-slate-300 hover:border-rose-400/40 hover:text-rose-300 flex items-center justify-center gap-1.5 transition-colors"><Trash2 size={11} /> Excluir</button>
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-5" data-testid="pastas-items-card">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h3 className="font-display text-white font-semibold text-sm flex-1 min-w-[150px]" data-testid="pastas-items-title">
              {detail ? "Detalhes" : folder || "Selecione uma pasta"} <span className="text-slate-500 font-normal text-xs">({detail ? (detail.versions || []).length : items?.length || 0} itens)</span>
              {!detail && folder?.includes("/") && (
                <span className="block text-[10px] font-mono2 text-slate-500 font-normal mt-0.5" data-testid="pastas-breadcrumb">{folder.split("/").join(" › ")}</span>
              )}
            </h3>
            <button data-testid="pastas-dups-btn" onClick={openDups}
              title="Deteta títulos duplicados ou quase iguais (1-2 caracteres, tokens CM/HD/etc.) e funde com segurança, poupando quota"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-[11px] font-semibold hover:bg-cyan-500/20 transition-colors">
              <Copy size={13} /> Duplicados
            </button>
            {!detail && (
              <select data-testid="pastas-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="input-dark rounded-lg px-3 py-2 text-xs" title="Ordenar">
                <option value="name">Nome A–Z</option>
                <option value="size">Maior tamanho</option>
                <option value="versions">Mais versões</option>
              </select>
            )}
            {!detail && (
              <div className="flex items-center gap-1.5">
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input data-testid="pastas-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={searchScope === "global" ? t("p_search_global") : t("p_search_here")} className="input-dark rounded-lg pl-8 pr-3 py-2 text-xs w-48" />
                </div>
                <button type="button" data-testid="pastas-search-scope" onClick={() => { setSearchScope(searchScope === "global" ? "folder" : "global"); setGlobalResults(null); }} title="Alternar: buscar só nesta pasta ou em todas as pastas" className={`px-2.5 py-2 rounded-lg text-[10px] font-semibold border transition-colors ${searchScope === "global" ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/10" : "border-slate-700 text-slate-400 hover:text-white"}`}>
                  {searchScope === "global" ? t("p_scope_global") : t("p_scope_folder")}
                </button>
              </div>
            )}
            {selCount > 0 && !detail && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="pastas-move-bar">
                <FolderInput size={14} className="text-amber-400" />
                <input data-testid="move-folder-search" value={moveFolderQ} onChange={(e) => setMoveFolderQ(e.target.value)} placeholder="Filtrar pasta..." className="input-dark rounded-lg px-3 py-2 text-xs w-32" />
                <select data-testid="move-to-select" className="input-dark rounded-lg px-3 py-2 text-xs max-w-56" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                  <option value="">Mover para: Sem pasta</option>
                  {kindFolders.filter((f) => f.name !== SEM_PASTA && f.name !== folder && (!moveFolderQ.trim() || f.name.toLowerCase().includes(moveFolderQ.trim().toLowerCase()))).map((f) => (
                    <option key={f.name} value={f.name}>{f.name}</option>
                  ))}
                </select>
                <button data-testid="move-confirm-btn" onClick={doMove} disabled={busy} className="btn-neon px-4 py-2 rounded-lg text-xs flex items-center gap-1.5">
                  {busy ? <Spinner /> : <>Mover {selCount}</>}
                </button>
                <button data-testid="bulk-delete-btn" onClick={() => setBulkDel(true)} className="px-4 py-2 rounded-lg text-xs border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 flex items-center gap-1.5 transition-colors">
                  <Trash2 size={13} /> Excluir {selCount}
                </button>
              </div>
            )}
          </div>
          {!detail && mixedOther > 0 && (
            <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="pastas-mixed-banner">
              <p className="text-xs text-amber-200 flex-1 min-w-44">
                Esta pasta tem <b>{mixedOther}</b> {kind === "tv" ? "filme(s)" : "série(s)"} misturado(s) — eles pertencem ao card {kind === "tv" ? "Filmes" : "Séries"} e não aparecem aqui.
              </p>
              <button data-testid="pastas-mixed-move-btn" onClick={moveMixed} disabled={busy} className="btn-neon px-4 py-2 rounded-lg text-xs flex items-center gap-1.5">
                {busy ? <Spinner /> : <>Mover para {kind === "tv" ? "Filmes" : "Séries"}/{folder}</>}
              </button>
            </div>
          )}
          {detail ? (
            <div className="space-y-3">
              {(detail.split_siblings || []).length > 0 && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="split-banner">
                  <p className="text-xs text-amber-200 flex-1 min-w-44">
                    Este título está <b>dividido</b>: há mais <b>{detail.split_siblings.reduce((a, s) => a + s.versions, 0)}</b> ficheiro(s) noutra(s) ficha(s) com o mesmo nome{detail.split_siblings[0].category ? ` (pasta "${detail.split_siblings[0].category}")` : ""} — por isso não aparecem aqui.
                  </p>
                  <button data-testid="split-merge-btn" onClick={mergeSplit} disabled={busy} className="btn-neon px-4 py-2 rounded-lg text-xs flex items-center gap-1.5">
                    {busy ? <Spinner /> : "Fundir tudo aqui"}
                  </button>
                </div>
              )}
              <TitleDetail
                detail={detail}
                onBack={closeDetail}
                onRename={() => { setItemModal({ type: "rename", item: { title_id: detail.id, title: detail.title, poster_url: detail.poster_url } }); setName(detail.title); setPosterUrl(detail.poster_url || ""); }}
                onDeleteTitle={() => setItemModal({ type: "delete", item: { title_id: detail.id, title: detail.title, versions: (detail.versions || []).length, size_bytes: (detail.versions || []).reduce((a, v) => a + (v.size_bytes || 0), 0) } })}
                onDeleteVersion={(v) => setDelVer({ ...v, title_id: detail.id })}
                onMoveVersion={moveVersion}
                onEditEpisode={(v) => setEpEdit({ version: v, season: v.season ?? 0, episode: v.episode ?? 0 })}
                onNewSeason={(s) => setSeasonImp({ mode: "create", season: s })}
                onImportSeason={(s) => setSeasonImp({ mode: "import", season: s, source: "urls", urls: "", m3u: "", version: "dual", quality: "FHD", startEp: "" })}
                onPcUpload={openPcUpload}
                onReupload={openReupload}
              />
            </div>
          ) : searchScope === "global" && search.trim().length >= 2 ? (
            !globalResults ? <Spinner /> : !globalResults.length ? (
              <EmptyState icon={Search} title={t("p_no_results")} subtitle={`${search.trim()}`} />
            ) : (
              <div className="space-y-1.5 max-h-[65vh] overflow-y-auto pr-1" data-testid="pastas-global-results">
                {globalResults.map((it) => (
                  <button key={it.title_id} type="button" data-testid={`pastas-global-${it.title_id}`}
                    onClick={() => { setFolder(it.folder); setSearchScope("folder"); setSearch(""); openDetail({ title_id: it.title_id }); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-800/60 hover:border-cyan-400/40 text-left transition-colors">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${it.media_type === "tv" ? "bg-amber-400/10 text-amber-300" : "bg-cyan-400/10 text-cyan-300"}`}>
                      {it.media_type === "tv" ? <Tv size={13} /> : <Film size={13} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs text-white truncate">{it.title}{it.year ? <span className="text-slate-500"> ({it.year})</span> : null}</span>
                      <span className="block text-[10px] font-mono2 text-slate-500 truncate">{it.folder} · {it.versions} versão(ões) · {fmtBytes(it.size_bytes)}</span>
                    </span>
                    <ChevronRight size={13} className="text-slate-600 shrink-0" />
                  </button>
                ))}
              </div>
            )
          ) : !folder ? (
            <EmptyState icon={FolderOpen} title={t("p_choose")} subtitle={t("p_choose_sub")} />
          ) : !items ? <Spinner /> : !items.length ? (
            <EmptyState icon={FolderOpen} title={t("p_empty")} subtitle={t("p_empty_sub")} />
          ) : (
            <div>
              <div className="flex flex-wrap items-center gap-2 px-1 mb-2" data-testid="pastas-items-toolbar">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800/40 bg-slate-950/40 cursor-pointer text-[11px] text-slate-400 hover:text-white transition-colors" data-testid="pastas-select-page-row">
                  <input type="checkbox" data-testid="pastas-select-page" checked={pageAllSelected} onChange={togglePage} className="accent-cyan-400" />
                  Página ({pagedItems.length})
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800/40 bg-slate-950/40 cursor-pointer text-[11px] text-slate-400 hover:text-white transition-colors" data-testid="pastas-select-all-row">
                  <input type="checkbox" data-testid="pastas-select-all" checked={allSelected} onChange={toggleAll} className="accent-cyan-400" />
                  Todos ({sortedItems.length})
                </label>
                {kind === "tv" && folder && (
                  <button data-testid="pastas-create-series" onClick={() => { setSeriesName(""); setSeriesModal(true); }}
                    title="Cria uma série vazia nesta pasta (com capa automática) — depois entre nela e importe temporadas/episódios"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[11px] font-semibold hover:bg-amber-500/20 transition-colors">
                    <Plus size={13} /> Criar série
                  </button>
                )}
                {selCount > 0 && <span className="text-[11px] font-semibold text-cyan-300" data-testid="pastas-sel-count">{selCount} selecionado(s)</span>}
                {totalItemPages > 1 && (
                  <div className="flex items-center gap-1 ml-auto" data-testid="pastas-items-pager">
                    <button data-testid="pastas-page-prev" onClick={() => setItemPage((p) => Math.max(1, p - 1))} disabled={safeItemPage <= 1} className="p-1.5 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-40"><ChevronLeft size={14} /></button>
                    <span className="text-[11px] font-mono2 text-slate-400" data-testid="pastas-page-info">{safeItemPage}/{totalItemPages}</span>
                    <button data-testid="pastas-page-next" onClick={() => setItemPage((p) => Math.min(totalItemPages, p + 1))} disabled={safeItemPage >= totalItemPages} className="p-1.5 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-40"><ChevronRight size={14} /></button>
                  </div>
                )}
              </div>
            <div className="space-y-1.5 max-h-[65vh] overflow-y-auto pr-1">
              {pagedItems.map((it) => (
                <label key={it.title_id} data-testid={`pastas-item-${it.title_id}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${selected[it.title_id] ? "border-cyan-400/40 bg-cyan-400/5" : "border-slate-800/60 hover:border-slate-700"}`}>
                  <input type="checkbox" data-testid={`pastas-check-${it.title_id}`} checked={!!selected[it.title_id]} onChange={() => setSelected({ ...selected, [it.title_id]: !selected[it.title_id] })} className="accent-cyan-400" />
                  {it.poster_url && !posterErr[it.title_id] ? (
                    <img src={it.poster_url} alt="" data-testid={`pastas-poster-${it.title_id}`} className="w-9 h-[52px] rounded-md object-cover shrink-0 bg-slate-800" loading="lazy"
                      onError={() => setPosterErr((prev) => ({ ...prev, [it.title_id]: true }))} />
                  ) : (
                    <button type="button" data-testid={`pastas-poster-ph-${it.title_id}`} disabled={posterBusy === it.title_id}
                      title="Sem capa visível — clica para tentar o TMDB; se não houver, gera a capa padrão VOD"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); retryPoster(it); }}
                      className={`w-9 h-[52px] rounded-md flex items-center justify-center shrink-0 transition-all hover:ring-2 hover:ring-amber-400/60 ${it.media_type === "tv" ? "bg-amber-400/10 text-amber-300" : "bg-cyan-400/10 text-cyan-300"}`}>
                      {posterBusy === it.title_id ? <Spinner /> : (it.media_type === "tv" ? <Tv size={14} /> : <Film size={14} />)}
                    </button>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs text-white truncate">{it.title} {it.year ? <span className="text-slate-500">({it.year})</span> : null}</span>
                    <span className="block text-[10px] font-mono2 text-slate-500">{it.versions} versão(ões) · {fmtBytes(it.size_bytes)}{it.res_label && <> · <span data-testid={`pastas-res-${it.title_id}`} className="text-amber-300 font-semibold" title={it.res_width && it.res_height ? `Resolução real: ${it.res_width}×${it.res_height}` : "Resolução real"}>{it.res_label} {it.res_height}p</span></>}</span>
                  </span>
                  <button type="button" data-testid={`pastas-open-${it.title_id}`} title="Abrir (temporadas e episódios)" onClick={(e) => { e.preventDefault(); openDetail(it); }} className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-colors shrink-0"><ChevronRight size={13} /></button>
                  <button type="button" data-testid={`pastas-rename-${it.title_id}`} title="Renomear" onClick={(e) => { e.preventDefault(); setItemModal({ type: "rename", item: it }); setName(it.title); setPosterUrl(it.poster_url || ""); }} className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors shrink-0"><Pencil size={13} /></button>
                  <button type="button" data-testid={`pastas-delete-${it.title_id}`} title="Excluir (apaga os arquivos da nuvem)" onClick={(e) => { e.preventDefault(); setItemModal({ type: "delete", item: it }); }} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-300 hover:bg-rose-400/10 transition-colors shrink-0"><Trash2 size={13} /></button>
                </label>
              ))}
            </div>
            </div>
          )}
        </GlassCard>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "create" ? "Nova pasta" : modal === "rename" ? `Renomear "${folder}"` : `Excluir "${folder}"?`}>
        {modal === "delete" ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">Os itens desta pasta voltam para <b>Sem pasta</b>. Nenhum arquivo é apagado.</p>
            <button data-testid="folder-delete-confirm" onClick={submitModal} disabled={busy} className="w-full px-4 py-3 rounded-xl text-sm font-semibold border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors flex items-center justify-center gap-2">
              {busy ? <Spinner /> : <><Trash2 size={14} /> Excluir pasta</>}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <input
              data-testid="folder-name-input"
              className="input-dark w-full rounded-xl px-4 py-3 text-sm"
              placeholder={modal === "create" ? "Ex: Ação ou Ação/2026 (subpasta)" : "Novo nome"}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <button data-testid="folder-save-btn" onClick={submitModal} disabled={busy || !name.trim()} className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
              {busy ? <Spinner /> : "Salvar"}
            </button>
          </div>
        )}
      </Modal>

      <Modal open={!!delVer} onClose={() => setDelVer(null)} title={delVer ? `Excluir ${delVer.season != null ? `S${String(delVer.season).padStart(2, "0")}E${String(delVer.episode || 0).padStart(2, "0")}` : "versão"}?` : ""} testid="pastas-delver-modal">
        {delVer && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">Isso apaga o arquivo da nuvem e libera {fmtBytes(delVer.size_bytes || 0)} de cota. Não tem volta.</p>
            <button data-testid="pastas-delver-confirm" onClick={deleteVersion} disabled={busy} className="w-full px-4 py-3 rounded-xl text-sm font-semibold border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors flex items-center justify-center gap-2">
              {busy ? <Spinner /> : <><Trash2 size={14} /> Excluir definitivamente</>}
            </button>
          </div>
        )}
      </Modal>

      <Modal open={bulkFolderDel} onClose={() => setBulkFolderDel(false)} title={`Excluir ${selFolderCount} pasta(s)?`} testid="pastas-bulkfolder-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-300">As <b>{selFolderCount} pasta(s) selecionadas</b> serão apagadas da organização. Os conteúdos dentro delas <b>não são apagados</b> — voltam para <b>Sem pasta</b>.</p>
          <button data-testid="bulk-folder-delete-confirm" onClick={doBulkFolderDelete} disabled={busy} className="w-full px-4 py-3 rounded-xl text-sm font-semibold border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors flex items-center justify-center gap-2">
            {busy ? <Spinner /> : <><Trash2 size={14} /> Excluir pastas</>}
          </button>
        </div>
      </Modal>

      <Modal open={bulkDel} onClose={() => setBulkDel(false)} title={`Excluir ${selCount} título(s)?`} testid="pastas-bulkdel-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-300">Isso apaga os <b>{selCount} título(s) selecionados</b> e todos os arquivos deles da nuvem, liberando cota na hora. Não tem volta.</p>
          <button data-testid="bulk-delete-confirm" onClick={doBulkDelete} disabled={busy} className="w-full px-4 py-3 rounded-xl text-sm font-semibold border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors flex items-center justify-center gap-2">
            {busy ? <Spinner /> : <><Trash2 size={14} /> Excluir definitivamente</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!epEdit} onClose={() => setEpEdit(null)} title={`Editar episódio — ${detail?.title || ""}`} testid="pastas-episode-modal">
        {epEdit && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Temporada</label>
                <input data-testid="episode-season-input" type="number" min="0" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={epEdit.season} onChange={(e) => setEpEdit({ ...epEdit, season: Number(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Episódio</label>
                <input data-testid="episode-episode-input" type="number" min="0" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={epEdit.episode} onChange={(e) => setEpEdit({ ...epEdit, episode: Number(e.target.value) })} />
              </div>
            </div>
            <p className="text-[10px] font-mono2 text-slate-600">Muda só a etiqueta do episódio (S/E) no catálogo — o ficheiro na nuvem mantém-se. Só a etiqueta visível é alterada.</p>
            <button data-testid="episode-save" onClick={saveEpisode} disabled={epBusy} className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
              {epBusy ? <Spinner /> : <><Pencil size={14} /> Guardar episódio</>}
            </button>
          </div>
        )}
      </Modal>

      <Modal open={!!seasonImp} onClose={() => setSeasonImp(null)} title={seasonImp?.mode === "create" ? `Nova temporada — ${detail?.title || ""}` : `Importar para temporada — ${detail?.title || ""}`} testid="season-modal">
        {seasonImp && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Temporada</label>
                <input data-testid="season-number-input" type="number" min="1" max="50" className="input-dark w-full rounded-xl px-4 py-3 text-sm"
                  value={seasonImp.season} onChange={(e) => setSeasonImp({ ...seasonImp, season: Math.max(1, Number(e.target.value) || 1) })} />
              </div>
              {seasonImp.mode === "import" && (
                <div>
                  <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Fonte</label>
                  <div className="flex rounded-xl overflow-hidden border border-slate-700">
                    {[["urls", "Links diretos"], ["m3u", "Lista M3U"]].map(([v, lbl]) => (
                      <button key={v} type="button" data-testid={`season-source-${v}`}
                        onClick={() => setSeasonImp({ ...seasonImp, source: v })}
                        className={`flex-1 px-3 py-3 text-[11px] font-mono2 uppercase transition-colors ${seasonImp.source === v ? "btn-neon" : "text-slate-400 hover:text-slate-200"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {seasonImp.mode === "import" && (
              <>
                {seasonImp.source === "m3u" ? (
                  <div>
                    <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Link da lista M3U do fornecedor</label>
                    <input data-testid="season-m3u-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2"
                      placeholder="http://fornecedor.com/get.php?username=...&type=m3u_plus"
                      value={seasonImp.m3u} onChange={(e) => setSeasonImp({ ...seasonImp, m3u: e.target.value })} />
                    <p className="text-[10px] font-mono2 text-slate-600 mt-1.5">O sistema filtra sozinho: só episódios DESTA série e DESTA temporada.</p>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Links dos episódios (um por linha)</label>
                    <textarea data-testid="season-urls-input" rows={5} className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2 resize-y"
                      placeholder={"http://.../episodio1.mp4\nhttp://.../episodio2.mp4"}
                      value={seasonImp.urls} onChange={(e) => setSeasonImp({ ...seasonImp, urls: e.target.value })} />
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] font-mono2 text-slate-600 flex-1">Se o nome tiver SxxExx uso-o; senão atribuo em sequência.</span>
                      <label className="text-[10px] font-mono2 uppercase text-slate-500 shrink-0">Começar no ep.</label>
                      <input data-testid="season-startep-input" type="number" min="1" placeholder="1"
                        className="input-dark w-20 rounded-lg px-2 py-1.5 text-xs"
                        value={seasonImp.startEp} onChange={(e) => setSeasonImp({ ...seasonImp, startEp: e.target.value })} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Versão</label>
                    <select data-testid="season-version-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm"
                      value={seasonImp.version} onChange={(e) => setSeasonImp({ ...seasonImp, version: e.target.value })}>
                      {["dual", "dublado", "legendado", "nacional"].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Qualidade</label>
                    <select data-testid="season-quality-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm"
                      value={seasonImp.quality} onChange={(e) => setSeasonImp({ ...seasonImp, quality: e.target.value })}>
                      {["FHD", "UHD4K", "HD", "SD"].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}
            <button data-testid="season-submit" onClick={submitSeason} disabled={seasonBusy}
              className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
              {seasonBusy ? <Spinner /> : seasonImp.mode === "create"
                ? <><Plus size={14} /> Criar temporada {seasonImp.season} e importar</>
                : <><Download size={14} /> Importar temporada {seasonImp.season}</>}
            </button>
            {seasonImp.mode === "create" && (
              <p className="text-[10px] font-mono2 text-slate-600">A temporada aparece assim que o primeiro episódio chegar — por isso o passo seguinte já é a importação.</p>
            )}
          </div>
        )}
      </Modal>

      <Modal open={!!pcUpload} onClose={() => { if (!seasonBusy) setPcUpload(null); }} title={`Upload do PC — ${detail?.title || ""} · Temporada ${pcUpload?.season || ""}`} testid="pc-upload-modal">
        {pcUpload && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Temporada de destino</label>
              <input data-testid="pc-upload-season" type="number" min="1" max="50" className="input-dark w-28 rounded-xl px-4 py-3 text-sm"
                value={pcUpload.season} onChange={(e) => setPcUpload({ ...pcUpload, season: Math.max(1, Number(e.target.value) || 1) })} disabled={seasonBusy} />
            </div>
            <input ref={pcFileRef} type="file" multiple accept="video/*,.mp4,.mkv,.avi,.m4v,.webm,.mov,.ts" className="hidden" onChange={(e) => { addPcFiles(e.target.files); e.target.value = ""; }} />
            <button type="button" data-testid="pc-upload-pick" onClick={() => pcFileRef.current?.click()} disabled={seasonBusy}
              className="w-full px-4 py-6 rounded-xl border-2 border-dashed border-slate-700 hover:border-cyan-400/50 text-slate-400 hover:text-cyan-300 text-sm flex flex-col items-center gap-2 transition-colors">
              <CloudUpload size={22} />
              Escolher ficheiros do computador
              <span className="text-[10px] font-mono2 text-slate-600">vídeo — vários de uma vez; se o nome tiver SxxExx uso esse episódio, senão numero em sequência</span>
            </button>
            {pcUpload.files.length > 0 && (
              <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1" data-testid="pc-upload-list">
                {pcUpload.files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-800/70">
                    <FileVideo size={14} className="text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-slate-300 truncate" title={f.name}>{f.name}</p>
                      {f.status === "uploading" ? (
                        <div className="h-1.5 rounded bg-slate-800 mt-1 overflow-hidden"><div className="h-full bg-cyan-400/70 transition-all" style={{ width: `${f.pct}%` }} /></div>
                      ) : f.err ? (
                        <p className="text-[9px] font-mono2 text-rose-300 truncate">{f.err}</p>
                      ) : (
                        <p className="text-[9px] font-mono2 text-slate-600">{fmtBytes(f.size)}</p>
                      )}
                    </div>
                    <span className="text-[9px] font-mono2 text-slate-500 shrink-0">E{String(f.episode).padStart(2, "0")}</span>
                    {f.status === "done" ? <CheckCircle2 size={14} className="text-emerald-300 shrink-0" />
                      : f.status === "error" ? <AlertIcon size={14} className="text-rose-300 shrink-0" />
                      : f.status === "uploading" ? <span className="text-[9px] font-mono2 text-cyan-300 shrink-0">{f.pct}%</span>
                      : <button type="button" data-testid={`pc-upload-remove-${i}`} onClick={() => setPcUpload({ ...pcUpload, files: pcUpload.files.filter((_, xi) => xi !== i) })} disabled={seasonBusy} className="text-slate-600 hover:text-rose-300 shrink-0"><Trash2 size={12} /></button>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              {seasonBusy ? (
                <button data-testid="pc-upload-cancel" onClick={() => { pcAbortRef.current = true; }} className="flex-1 px-4 py-3 rounded-xl text-sm border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors">Parar uploads</button>
              ) : (
                <button data-testid="pc-upload-start" onClick={startPcUpload} disabled={!pcUpload.files.length || seasonBusy} className="btn-neon flex-1 px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
                  <CloudUpload size={14} /> Enviar {pcUpload.files.length ? `${pcUpload.files.length} episódio(s)` : ""} para T{String(pcUpload.season).padStart(2, "0")}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!reup} onClose={() => { if (!seasonBusy) setReup(null); }} title={`Re-upload — ${detail?.title || ""}${detail?.kind === "series" ? ` · S${String(reup?.season || 0).padStart(2, "0")}E${String(reup?.episode || 0).padStart(2, "0")}` : ""}`} testid="reupload-modal">
        {reup && (
          <div className="space-y-4">
            <p className="text-[11px] text-slate-400">Substitui o ficheiro na nuvem (ficheiro corrompido/errado). <b className="text-emerald-300">O link do cliente NÃO muda.</b></p>
            {detail.kind === "series" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Temporada</label>
                  <input data-testid="reupload-season" type="number" min="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={reup.season} onChange={(e) => setReup({ ...reup, season: Math.max(1, Number(e.target.value) || 1) })} disabled={seasonBusy} />
                </div>
                <div>
                  <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Episódio</label>
                  <input data-testid="reupload-episode" type="number" min="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={reup.episode} onChange={(e) => setReup({ ...reup, episode: Math.max(1, Number(e.target.value) || 1) })} disabled={seasonBusy} />
                </div>
              </div>
            )}
            <input id="reupload-file" type="file" accept="video/*,.mp4,.mkv,.avi,.m4v,.webm,.mov,.ts" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setReup({ ...reup, file: f }); e.target.value = ""; }} />
            <label htmlFor="reupload-file" data-testid="reupload-pick" className="w-full px-4 py-6 rounded-xl border-2 border-dashed border-slate-700 hover:border-emerald-400/50 text-slate-400 hover:text-emerald-300 text-sm flex flex-col items-center gap-2 transition-colors cursor-pointer">
              <CloudUpload size={22} />
              {reup.file ? `${reup.file.name} · ${fmtBytes(reup.file.size)}` : "Escolher o ficheiro corrigido"}
            </label>
            {seasonBusy && <div className="h-1.5 rounded bg-slate-800 overflow-hidden"><div className="h-full bg-emerald-400/70 transition-all" style={{ width: `${reup.pct}%` }} /></div>}
            <button data-testid="reupload-start" onClick={startReupload} disabled={!reup.file || seasonBusy} className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
              {seasonBusy ? `A enviar ${reup.pct}%` : <><RefreshCw size={14} /> Substituir ficheiro (link mantém-se)</>}
            </button>
          </div>
        )}
      </Modal>

      <Modal open={dupsOpen} onClose={() => setDupsOpen(false)} title="Títulos duplicados — fusão segura" testid="dups-modal">
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/5 p-3 space-y-2" data-testid="dups-repair-card">
            <p className="text-[11px] text-slate-400"><b className="text-cyan-300">Reparação automática:</b> funde só fichas com o nome EXATO igual (ex.: série dividida em duas entradas "The 100") e repõe ficheiros presos a fichas arquivadas. Os quase-iguais abaixo continuam a precisar da tua confirmação.</p>
            <button data-testid="dups-repair-btn" onClick={repairSplit} disabled={busy}
              className="w-full px-3 py-2 rounded-lg text-[11px] font-semibold border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/15 transition-colors flex items-center justify-center gap-2">
              {busy ? <Spinner /> : "Reparar agora (só nomes exatos)"}
            </button>
          </div>
          {dups === null && <div className="flex justify-center py-8"><Spinner /></div>}
          {dups !== null && dups.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Sem duplicados — cada título existe uma só vez.</p>}
          {Array.isArray(dups) && dups.length > 1 && (
            <button data-testid="dup-merge-all" disabled={busy} onClick={mergeAll}
              className="btn-violet w-full px-3 py-2.5 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-2">
              {busy ? <Spinner /> : <><Copy size={12} /> Fundir TODOS ({dups.length} grupos — mantém o que tem mais ficheiros)</>}
            </button>
          )}
          {(dups || []).map((g, gi) => (
            <div key={gi} className="rounded-xl border border-[#232D42] p-3 space-y-2" data-testid={`dup-group-${gi}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-white truncate">{g.title} {g.year ? <span className="text-slate-500">({g.year})</span> : null}</p>
                <span className="text-[10px] font-mono2 text-amber-300 shrink-0">poupa {fmtBytes(g.savable_bytes)}</span>
              </div>
              {g.items.map((it) => (
                <label key={it.id} className="flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
                  <input type="radio" name={`dupkeep-${gi}`} data-testid={`dup-keep-${gi}-${it.id}`} checked={(dupKeep[gi] || g.items[0].id) === it.id} onChange={() => setDupKeep({ ...dupKeep, [gi]: it.id })} className="accent-amber-500" />
                  <span className="flex-1 truncate">{it.category || "(Sem pasta)"} · {it.versions} versão(ões) · {fmtBytes(it.size_bytes)}</span>
                </label>
              ))}
              <p className="text-[10px] text-slate-500">Mantém o título escolhido, move os ficheiros (links intactos), resolve conflitos ficando com a melhor qualidade e apaga da nuvem só as cópias não partilhadas.</p>
              <button data-testid={`dup-merge-${gi}`} onClick={() => mergeGroup(g, gi)} disabled={busy}
                className="btn-neon w-full px-3 py-2 rounded-lg text-[11px] flex items-center justify-center gap-2">
                {busy ? <Spinner /> : <><Copy size={12} /> Fundir no título selecionado</>}
              </button>
            </div>
          ))}
        </div>
      </Modal>

      <Modal open={seriesModal} onClose={() => { if (!busy) setSeriesModal(false); }} title={`Criar série em “${folder}”`} testid="create-series-modal">
        <div className="space-y-4">
          <p className="text-[11px] text-slate-400">A série é criada vazia e a capa é gerada automaticamente (TMDB ou capa padrão). Depois entre nela e use <b className="text-slate-200">+ Temporada / Importar / Upload do PC</b> para adicionar os episódios no sítio certo.</p>
          <input data-testid="create-series-name" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Nome da série (ex.: Carrie)"
            value={seriesName} onChange={(e) => setSeriesName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createSeries(); }} autoFocus />
          <button data-testid="create-series-save" onClick={createSeries} disabled={busy || !seriesName.trim()} className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
            {busy ? <Spinner /> : <><Plus size={14} /> Criar série</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!itemModal} onClose={() => setItemModal(null)} title={itemModal?.type === "rename" ? `Renomear "${itemModal.item.title}"` : `Excluir "${itemModal?.item?.title}"?`}>
        {itemModal && (itemModal.type === "delete" ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">Isso apaga <b>{itemModal.item.versions} versão(ões)</b> e os arquivos da nuvem, liberando {fmtBytes(itemModal.item.size_bytes)} de cota. Não tem volta.</p>
            <button data-testid="item-delete-confirm" onClick={submitItemModal} disabled={busy} className="w-full px-4 py-3 rounded-xl text-sm font-semibold border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors flex items-center justify-center gap-2">
              {busy ? <Spinner /> : <><Trash2 size={14} /> Excluir definitivamente</>}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1">Nome</label>
              <input data-testid="item-rename-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1">Link da capa (opcional — http/https)</label>
              <div className="flex items-start gap-3">
                <input data-testid="item-poster-input" className="input-dark flex-1 rounded-xl px-4 py-3 text-xs font-mono2" placeholder="https://…/capa.jpg" value={posterUrl} onChange={(e) => setPosterUrl(e.target.value)} />
                {(posterUrl.trim() || itemModal.item.poster_url) && (
                  <img data-testid="item-poster-preview" src={posterUrl.trim() || itemModal.item.poster_url} alt="" className="w-12 h-[72px] rounded-lg border border-[#232D42] object-cover shrink-0" onError={(e) => { e.currentTarget.style.opacity = "0.25"; }} onLoad={(e) => { e.currentTarget.style.opacity = "1"; }} />
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5">Em séries, a nova capa aplica-se também a todos os episódios. Deixe vazio para manter a atual.</p>
            </div>
            <button data-testid="item-rename-save" onClick={submitItemModal} disabled={busy || !name.trim()} className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">{busy ? <Spinner /> : "Salvar"}</button>
          </div>
        ))}
      </Modal>
    </div>
  );
}
